/**
 * @file CompanyInvoicesFilters.tsx
 * @brief Filter bar for company-scoped invoices table.
 *
 * @details
 * Flush `GridCells` layout matched to Sessions/Users filters (29-07-2026).
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — end-to-end GridCell filters
 */

import { useEffect, useState } from "react";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import { EmployeePicker } from "@/features/company-sessions/components/EmployeePicker";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import type { CompanyInvoicesListParams, InvoicePeriod } from "../schemas";

const PERIOD_LABELS: Record<InvoicePeriod, string> = {
  mtd: "This month",
  "3m": "Last 3 months",
  all: "All time",
};

interface Props {
  value: CompanyInvoicesListParams;
  onChange: (next: CompanyInvoicesListParams) => void;
}

/**
 * Period, employee and dates as the page header's pills (DES 01-10-2026:
 * "baqi pages pe bhi yehi pills laga do"). A custom range overrides the
 * period, as before: the period pill then reads "Custom dates".
 */
export function CompanyInvoicesHeaderFilters({ value, onChange }: Props) {
  const hasCustomDates = Boolean(value.from || value.to);
  const period = value.period ?? "all";
  const periodItems = (["all", "mtd", "3m"] as InvoicePeriod[]).map((key) => ({
    value: key,
    label: PERIOD_LABELS[key],
  }));

  return (
    <>
      <HeaderFilterMenu
        title="Filter by period"
        searchPlaceholder="Search periods"
        items={periodItems}
        selected={hasCustomDates ? "" : period}
        label={hasCustomDates ? "Custom dates" : PERIOD_LABELS[period]}
        active={!hasCustomDates && period !== "all"}
        onClear={() => onChange({ ...value, period: "all", page: 1 })}
        onSelect={(v) =>
          onChange({
            ...value,
            period: v as InvoicePeriod,
            from: undefined,
            to: undefined,
            page: 1,
          })
        }
      />
      <EmployeePicker
        variant="pill"
        value={value.employee_id ?? null}
        onChange={(id) => onChange({ ...value, employee_id: id ?? undefined, page: 1 })}
      />
      <DateRangePicker
        variant="circle"
        from={value.from}
        to={value.to}
        onChange={({ from, to }) =>
          onChange({
            ...value,
            from,
            to,
            period: from || to ? undefined : value.period,
            page: 1,
          })
        }
      />
    </>
  );
}

/** The search, rendered inside the page's bottom `PageDock`. */
export function CompanyInvoicesFilters({ value, onChange }: Props) {
  const [searchInput, setSearchInput] = useState(value.q ?? "");
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    if ((value.q ?? "") === debouncedSearch) return;
    onChange({ ...value, q: debouncedSearch || undefined, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // The Notes screen's search (DES 01-10-2026), in the page's bottom dock.
  return (
    <ExpandingSearch
      value={searchInput}
      onChange={setSearchInput}
      placeholder="Invoice ID, employee name, or email"
      label="Search invoices"
    />
  );
}
